import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import Home from "./pages/Home";
import Auth from "./pages/Auth";
import Demo from "./pages/Demo";
import DemoSucesso from "./pages/DemoSucesso";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordDebugger from "./components/ResetPasswordDebugger";
import Intranet from "./pages/Intranet";
import IntranetDashboard from "./pages/IntranetDashboard";
import Contratacao from "./pages/Contratacao";
import Mapa from "./pages/Mapa";

import NotFound from "./pages/NotFound";
import NoOrganization from "./pages/NoOrganization";
import Permissoes from "./pages/intranet/Permissoes";
import Ariana from "./pages/intranet/Ariana";
import Conhecimento from "./pages/intranet/Conhecimento";
import LogSistema from "./pages/intranet/LogSistema";
import TipoClientes from "./pages/intranet/TipoClientes";
import Clientes from "./pages/intranet/Clientes";
import TipoLocal from "./pages/intranet/TipoLocal";
import Locais from "./pages/intranet/Locais";
import Contatos from "./pages/intranet/Contatos";
import ProdutosInsumos from "./pages/intranet/ProdutosInsumos";
import MovimentacoesEstoque from "./pages/intranet/MovimentacoesEstoque";
import EstoqueBaixo from "./pages/intranet/EstoqueBaixo";
import Fretes from "./pages/intranet/Fretes";
import TipoContrato from "./pages/intranet/TipoContrato";
import ModelosContrato from "./pages/intranet/ModelosContrato";
import ModeloContratoForm from "./pages/intranet/ModeloContratoForm";
import ModeloContratoView from "./pages/intranet/ModeloContratoView";
import Fornecedores from "./pages/intranet/Fornecedores";
import FornecedorForm from "./pages/intranet/FornecedorForm";
import FornecedorView from "./pages/intranet/FornecedorView";
import ClientesNew from "./pages/intranet/ClientesNew";
import ClienteView from "./pages/intranet/ClienteView";
import ClienteEdit from "./pages/intranet/ClienteEdit";
import TipoProposta from "./pages/intranet/TipoProposta";
import ModelosProposta from "./pages/intranet/ModelosProposta";
import ModeloPropostaForm from "./pages/intranet/ModeloPropostaForm";
import ModeloPropostaView from "./pages/intranet/ModeloPropostaView";
import ContratacaoList from "./pages/intranet/ContratacaoList";
import ContratacaoView from "./pages/intranet/ContratacaoView";
import ContratacaoEdit from "./pages/intranet/ContratacaoEdit";
import Cargos from "./pages/intranet/Cargos";
import Empresas from "./pages/intranet/Empresas";
import EmpresaForm from "./pages/intranet/EmpresaForm";
import Produtos from "./pages/intranet/Produtos";
import Categorias from "./pages/intranet/Categorias";
import Estoque from "./pages/intranet/Estoque";
import Movimentacoes from "./pages/intranet/Movimentacoes";
import PropostasProjetos from "./pages/intranet/PropostasProjetos";
import PropostasOrcamentos from "./pages/intranet/PropostasOrcamentos";
import PropostasImpostos from "./pages/intranet/PropostasImpostos";
import ProjetoForm from "./pages/intranet/ProjetoForm";
import ProjetoView from "./pages/intranet/ProjetoView";
import OrcamentoForm from "./pages/intranet/OrcamentoForm";
import OrcamentoView from "./pages/intranet/OrcamentoView";
import Organizacoes from "./pages/intranet/Organizacoes";
import OrganizacaoMembros from "./pages/intranet/OrganizacaoMembros";
import SuperAdmin from "./pages/intranet/SuperAdmin";
import ConfiguracaoPlanos from "./pages/intranet/ConfiguracaoPlanos";
import PlanDetail from "./pages/plans/PlanDetail";
import PlanCheckout from "./pages/plans/PlanCheckout";
import PaymentSuccess from "./pages/PaymentSuccess";
import CreateOrganizationAfterPayment from "./pages/CreateOrganizationAfterPayment";
import Success from "./pages/Success";
import FluxoContratacoes from "./pages/intranet/FluxoContratacoes";
import LogEvolucaoContratacoes from "./pages/intranet/LogEvolucaoContratacoes";
import RequestRejected from "./pages/RequestRejected";
import PendingRequest from "./pages/PendingRequest";
import EmailConfirmation from "./pages/EmailConfirmation";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <div className="notranslate" translate="no">
          <Toaster />
          <Sonner />
        </div>
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/demo/sucesso" element={<DemoSucesso />} />
            <Route path="/mapa" element={<Mapa />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/debug-reset" element={<ResetPasswordDebugger />} />
            <Route path="/intranet" element={<Intranet />}>
              <Route index element={<FluxoContratacoes />} />
              {/* Placeholder routes for intranet modules */}
              <Route path="usuarios" element={<div>Usuários - Em desenvolvimento</div>} />
              <Route path="tipo-usuarios" element={<div>Tipo Usuários - Em desenvolvimento</div>} />
              <Route path="permissoes" element={<Permissoes />} />
              <Route path="pedidos-acesso" element={<div>Pedidos de Acesso - Em desenvolvimento</div>} />
              <Route path="log-sistema" element={<LogSistema />} />
              <Route path="ariana" element={<Ariana />} />
              <Route path="conhecimento" element={<Conhecimento />} />
              <Route path="empresas" element={<Empresas />} />
              <Route path="empresas/nova" element={<EmpresaForm />} />
              <Route path="empresas/:id" element={<div>Visualizar Empresa - Em desenvolvimento</div>} />
              <Route path="empresas/:id/editar" element={<EmpresaForm />} />
              <Route path="tipo-clientes" element={<TipoClientes />} />
              <Route path="clientes" element={<Clientes />} />
              <Route path="tipo-locais" element={<TipoLocal />} />
              <Route path="locais" element={<Locais />} />
              <Route path="contatos" element={<Contatos />} />
              <Route path="produtosinsumos" element={<ProdutosInsumos />} />
              <Route path="movimentacoes-estoque" element={<MovimentacoesEstoque />} />
              <Route path="estoque-baixo" element={<EstoqueBaixo />} />
              <Route path="fretes" element={<Fretes />} />
              <Route path="recursos-humanos/tipo-contrato" element={<TipoContrato />} />
              <Route path="recursos-humanos/modelos-contrato" element={<ModelosContrato />} />
              <Route path="recursos-humanos/modelos-contrato/novo" element={<ModeloContratoForm />} />
              <Route path="recursos-humanos/modelos-contrato/:id/editar" element={<ModeloContratoForm />} />
              <Route path="recursos-humanos/modelos-contrato/:id/visualizar" element={<ModeloContratoView />} />
              <Route path="recursos-humanos/cargos" element={<Cargos />} />
              <Route path="contratacao" element={<ContratacaoList />} />
              <Route path="contratacao/:id/view" element={<ContratacaoView />} />
              <Route path="contratacao/:id/edit" element={<ContratacaoEdit />} />
              <Route path="tipo-proposta" element={<TipoProposta />} />
              <Route path="modelos-proposta" element={<ModelosProposta />} />
              <Route path="modelos-proposta/novo" element={<ModeloPropostaForm />} />
              <Route path="modelos-proposta/:id/editar" element={<ModeloPropostaForm />} />
              <Route path="modelos-proposta/:id" element={<ModeloPropostaView />} />
              <Route path="fornecedores" element={<Fornecedores />} />
              <Route path="fornecedores/novo" element={<FornecedorForm />} />
              <Route path="fornecedores/:id" element={<FornecedorView />} />
              <Route path="fornecedores/:id/edit" element={<FornecedorForm />} />
              <Route path="clientes-new" element={<ClientesNew />} />
              <Route path="clientes/:id" element={<ClienteView />} />
              <Route path="clientes/:id/edit" element={<ClienteEdit />} />
              <Route path="produtos" element={<Produtos />} />
              <Route path="categorias" element={<Categorias />} />
              <Route path="estoque" element={<Estoque />} />
              <Route path="movimentacoes" element={<Movimentacoes />} />
              <Route path="propostas/projetos" element={<PropostasProjetos />} />
              <Route path="propostas/projetos/new" element={<ProjetoForm />} />
              <Route path="propostas/projetos/edit/:id" element={<ProjetoForm />} />
              <Route path="propostas/projetos/view/:id" element={<ProjetoView />} />
              <Route path="propostas/orcamentos" element={<PropostasOrcamentos />} />
              <Route path="propostas/orcamentos/new" element={<OrcamentoForm />} />
              <Route path="propostas/orcamentos/:id" element={<OrcamentoView />} />
              <Route path="propostas/orcamentos/:id/edit" element={<OrcamentoForm />} />
              <Route path="propostas/impostos" element={<PropostasImpostos />} />
              <Route path="organizacoes" element={<Organizacoes />} />
              <Route path="organizacoes/:orgId/membros" element={<OrganizacaoMembros />} />
              <Route path="relatorios/fluxo-contratacoes" element={<FluxoContratacoes />} />
              <Route path="relatorios/log-evolucao" element={<LogEvolucaoContratacoes />} />
              <Route path="super-admin" element={<SuperAdmin />} />
              <Route path="configuracoes/planos" element={<ConfiguracaoPlanos />} />
              {/* Add more routes as needed */}
            </Route>
            <Route path="/contratacao" element={<Contratacao />} />
            <Route path="/plans/:planId" element={<PlanDetail />} />
            <Route path="/plans/:planId/checkout" element={<PlanCheckout />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/create-organization-after-payment" element={<CreateOrganizationAfterPayment />} />
            <Route path="/success" element={<Success />} />
            <Route path="/no-organization" element={<NoOrganization />} />
            <Route path="/pending-request" element={<PendingRequest />} />
            <Route path="/email-confirmation" element={<EmailConfirmation />} />
            <Route path="/request-rejected" element={<RequestRejected />} />
            <Route path="/" element={<Home />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
